import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { IconBrandYoutube, IconZoomIn, IconZoomOut, IconPlayerPlay, IconLoader2, IconFileText, IconListDetails } from "@tabler/icons-react";
import { useJournalBlocks } from '@/hooks/useJournalBlocks';

export interface TranscriptSegment {
  text: string;
  start: number;
  duration: number;
}

interface YouTubeAnalysis {
  summary: string;
  key_points: string[];
  topics: string[];
  learning_objectives: string[];
}

interface YouTubeTabViewProps {
  videoId: string;
  videoUrl?: string;
  journalId?: string;
  title?: string;
}

// 10% increments from 50% to 200%
const ZOOM_LEVELS = [0.5, 0.6, 0.7, 0.8, 0.9, 1, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9, 2];

// Format seconds to MM:SS
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

interface Paragraph {
  startTime: number;
  segments: TranscriptSegment[];
}

// Group segments into paragraphs ending at sentence boundaries
const groupIntoParagraphs = (segments: TranscriptSegment[]): Paragraph[] => {
  if (segments.length === 0) return [];

  const paragraphs: Paragraph[] = [];
  let currentParagraph: Paragraph = { startTime: segments[0].start, segments: [segments[0]] };

  const endsWithSentence = (text: string) => /[.!?]$/.test(text.trim());
  const MAX_PARAGRAPH_DURATION = 12; // Max seconds per paragraph (shorter = more breaks)

  for (let i = 1; i < segments.length; i++) {
    const lastSeg = currentParagraph.segments[currentParagraph.segments.length - 1];
    const paragraphDuration = segments[i].start - currentParagraph.startTime;

    // Start new paragraph if: last segment ended with sentence AND (pause > 1s OR duration > max)
    const prevEnd = lastSeg.start + lastSeg.duration;
    const gap = segments[i].start - prevEnd;

    if (endsWithSentence(lastSeg.text) && (gap > 1 || paragraphDuration > MAX_PARAGRAPH_DURATION)) {
      paragraphs.push(currentParagraph);
      currentParagraph = { startTime: segments[i].start, segments: [segments[i]] };
    } else {
      currentParagraph.segments.push(segments[i]);
    }
  }

  // Don't forget the last paragraph
  if (currentParagraph.segments.length > 0) {
    paragraphs.push(currentParagraph);
  }

  return paragraphs;
};

export const YouTubeTabView = ({ videoId, videoUrl, journalId, title }: YouTubeTabViewProps) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [autoScroll, setAutoScroll] = useState(true);
  const [activeTab, setActiveTab] = useState<'summary' | 'transcript'>('transcript');
  const playerRef = useRef<any>(null);
  const transcriptContainerRef = useRef<HTMLDivElement>(null);
  const activeSegmentRef = useRef<HTMLSpanElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Fetch blocks from DB
  const { data: journalBlocks, isLoading } = useJournalBlocks(journalId);

  // Find transcript and analysis from matching block
  const { transcript, analysis } = useMemo(() => {
    if (!journalBlocks || !videoId) return { transcript: [], analysis: null };

    for (const block of journalBlocks) {
      if (block.block_type !== 'youtube') continue;

      const content = block.content as {
        videoUrl?: string;
        transcript?: TranscriptSegment[];
        video_id?: string;
        analysis?: YouTubeAnalysis;
      } | null;

      if (!content) continue;

      if (content.video_id === videoId) {
        return { transcript: content.transcript || [], analysis: content.analysis || null };
      }

      if (content.videoUrl) {
        const match = content.videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([^&\n?#]+)/);
        if (match?.[1] === videoId) {
          return { transcript: content.transcript || [], analysis: content.analysis || null };
        }
      }
    }

    return { transcript: [], analysis: null };
  }, [journalBlocks, videoId]);

  // Group transcript into paragraphs
  const paragraphs = useMemo(() => groupIntoParagraphs(transcript), [transcript]);

  // Find active segment index (flat across all segments)
  const activeSegmentIndex = transcript.findIndex((seg, idx) => {
    const nextSeg = transcript[idx + 1];
    const segEnd = nextSeg ? nextSeg.start : seg.start + seg.duration;
    return currentTime >= seg.start && currentTime < segEnd;
  });

  // Computed values for render
  const hasTranscript = paragraphs.length > 0;
  const hasSummary = analysis?.summary;
  const videoMaxWidth = 896 * zoomLevel;

  // Initialize YouTube IFrame API
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    }

    const initPlayer = () => {
      if (window.YT && window.YT.Player && iframeRef.current) {
        playerRef.current = new window.YT.Player(iframeRef.current, {
          events: { onStateChange: () => startTimePolling() }
        });
      }
    };

    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      (window as any).onYouTubeIframeAPIReady = initPlayer;
    }

    return () => { playerRef.current = null; };
  }, [videoId]);

  // Poll for current time
  const timeIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const startTimePolling = useCallback(() => {
    if (timeIntervalRef.current) return;
    timeIntervalRef.current = setInterval(() => {
      if (playerRef.current?.getCurrentTime) {
        try {
          setCurrentTime(playerRef.current.getCurrentTime());
        } catch { /* Player not ready */ }
      }
    }, 250); // Faster polling for smoother highlight
  }, []);

  useEffect(() => {
    startTimePolling();
    return () => {
      if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
    };
  }, [startTimePolling]);

  // Auto-scroll to active segment - center in transcript container
  useEffect(() => {
    if (autoScroll && activeSegmentRef.current && transcriptContainerRef.current) {
      const container = transcriptContainerRef.current;
      const segment = activeSegmentRef.current;

      // Calculate position to center the segment in the container
      const containerHeight = container.clientHeight;
      const segmentTop = segment.offsetTop - container.offsetTop;
      const segmentHeight = segment.offsetHeight;

      // Scroll so segment is centered
      const scrollTarget = segmentTop - (containerHeight / 2) + (segmentHeight / 2);

      container.scrollTo({
        top: scrollTarget,
        behavior: 'smooth'
      });
    }
  }, [activeSegmentIndex, autoScroll]);

  const handleSeek = useCallback((time: number) => {
    if (playerRef.current?.seekTo) {
      playerRef.current.seekTo(time, true);
      setCurrentTime(time);
    }
  }, []);

  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => {
      const idx = ZOOM_LEVELS.indexOf(prev);
      return idx < ZOOM_LEVELS.length - 1 ? ZOOM_LEVELS[idx + 1] : prev;
    });
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => {
      const idx = ZOOM_LEVELS.indexOf(prev);
      return idx > 0 ? ZOOM_LEVELS[idx - 1] : prev;
    });
  }, []);

  // Pre-compute segment start indices for each paragraph
  const paragraphSegmentOffsets = useMemo(() => {
    let offset = 0;
    return paragraphs.map(para => {
      const startOffset = offset;
      offset += para.segments.length;
      return startOffset;
    });
  }, [paragraphs]);

  return (
    <div className="h-full flex flex-col bg-[#F8F8F6] overflow-hidden">
      {/* Header - full width, responsive padding */}
      <div className="flex-shrink-0 px-4 md:px-6 py-3 border-b border-slate-200/60">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <IconBrandYoutube className="w-5 h-5 text-red-500 shrink-0" />
            <h2 className="text-base md:text-lg font-semibold text-slate-800 truncate">{title || 'YouTube Video'}</h2>
          </div>

          {/* Zoom controls - hidden on mobile */}
          <div className="hidden md:flex items-center gap-1">
            <button
              onClick={handleZoomOut}
              disabled={zoomLevel === ZOOM_LEVELS[0]}
              className={`p-1.5 rounded transition-colors ${zoomLevel === ZOOM_LEVELS[0] ? 'text-slate-300' : 'text-slate-500 hover:bg-slate-200/50'
                }`}
            >
              <IconZoomOut className="w-4 h-4" />
            </button>
            <span className="text-xs text-slate-400 min-w-[3rem] text-center">{Math.round(zoomLevel * 100)}%</span>
            <button
              onClick={handleZoomIn}
              disabled={zoomLevel === ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
              className={`p-1.5 rounded transition-colors ${zoomLevel === ZOOM_LEVELS[ZOOM_LEVELS.length - 1] ? 'text-slate-300' : 'text-slate-500 hover:bg-slate-200/50'
                }`}
            >
              <IconZoomIn className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Content area with flex layout - responsive padding */}
      <div className="flex-1 flex flex-col overflow-hidden px-4 md:px-6 pt-4 md:pt-6">
        <div className="flex flex-col flex-1 min-h-0 mx-auto" style={{ maxWidth: videoMaxWidth }}>
          {/* 1. Video (fixed) */}
          <div className="flex-shrink-0 mb-4">
            <div className="aspect-video rounded-xl overflow-hidden bg-black shadow-lg">
              <iframe
                ref={iframeRef}
                id={`youtube-player-${videoId}`}
                src={`https://www.youtube.com/embed/${videoId}?enablejsapi=1&origin=${window.location.origin}`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </div>

          {/* 2. Tabs + Content (scrollable) */}
          {(hasSummary || hasTranscript) && (
            <div className="flex flex-col flex-1 min-h-0">
              {/* Tab buttons (fixed) - scrollable on mobile */}
              <div className="flex-shrink-0 flex items-center gap-2 md:gap-4 pb-3 border-b border-slate-200/60 overflow-x-auto scrollbar-hide">
                {hasSummary && (
                  <button
                    onClick={() => setActiveTab('summary')}
                    className={`flex items-center gap-1.5 md:gap-2 px-1 py-1 text-sm font-medium transition-colors shrink-0 ${activeTab === 'summary'
                      ? 'text-slate-800 border-b-2 border-slate-800 -mb-[13px] pb-[11px]'
                      : 'text-slate-400 hover:text-slate-600'
                      }`}
                  >
                    <IconFileText className="w-4 h-4" />
                    Summary
                  </button>
                )}
                {hasTranscript && (
                  <button
                    onClick={() => setActiveTab('transcript')}
                    className={`flex items-center gap-1.5 md:gap-2 px-1 py-1 text-sm font-medium transition-colors shrink-0 ${activeTab === 'transcript'
                      ? 'text-slate-800 border-b-2 border-slate-800 -mb-[13px] pb-[11px]'
                      : 'text-slate-400 hover:text-slate-600'
                      }`}
                  >
                    <IconListDetails className="w-4 h-4" />
                    <span className="hidden md:inline">Transcript</span>
                    <span className="md:hidden">Trans.</span>
                    <span className="text-xs text-slate-400 font-normal hidden md:inline">
                      {transcript.length} segments
                    </span>
                  </button>
                )}

                {/* Auto-scroll toggle - abbreviated on mobile */}
                {activeTab === 'transcript' && hasTranscript && (
                  <div className="ml-auto shrink-0">
                    <button
                      onClick={() => setAutoScroll(!autoScroll)}
                      className={`flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${autoScroll
                        ? 'bg-emerald-100/80 text-emerald-700'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200/80'
                        }`}
                    >
                      <IconPlayerPlay className="w-3.5 h-3.5" />
                      <span className="hidden md:inline">Auto Scroll {autoScroll ? 'On' : 'Off'}</span>
                      <span className="md:hidden">{autoScroll ? 'On' : 'Off'}</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Tab content - scrollable flex child */}
              <div className="flex-1 overflow-auto pt-4" ref={transcriptContainerRef} onMouseEnter={() => setAutoScroll(false)}>
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex items-center gap-2 text-slate-400">
                      <IconLoader2 className="w-5 h-5 animate-spin" />
                      <span>Loading...</span>
                    </div>
                  </div>
                ) : activeTab === 'summary' && analysis ? (
                  /* Summary Tab */
                  <div className="space-y-6">
                    <p className="text-slate-600 leading-relaxed">{analysis.summary}</p>

                    {analysis.key_points.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">Key Points</h4>
                        <ul className="space-y-2">
                          {analysis.key_points.map((point, i) => (
                            <li key={i} className="flex items-start gap-2 text-slate-600">
                              <span className="text-emerald-500 mt-1">•</span>
                              <span className="leading-relaxed">{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {analysis.topics.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">Topics</h4>
                        <div className="flex flex-wrap gap-2">
                          {analysis.topics.map((topic, i) => (
                            <span key={i} className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm">
                              {topic}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {analysis.learning_objectives.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">What You'll Learn</h4>
                        <ul className="space-y-2">
                          {analysis.learning_objectives.map((obj, i) => (
                            <li key={i} className="flex items-start gap-2 text-slate-600">
                              <span className="text-blue-500 mt-1">✓</span>
                              <span className="leading-relaxed">{obj}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : activeTab === 'transcript' && hasTranscript ? (
                  /* Transcript Tab - paragraphs with clickable segments */
                  <div className="space-y-6">
                    {paragraphs.map((para, paraIndex) => (
                      <div key={paraIndex} className="group">
                        {/* Paragraph timestamp */}
                        <div className="text-xs font-mono text-slate-400 mb-2">
                          {formatTime(para.startTime)}
                        </div>

                        {/* Paragraph text with individually clickable/highlightable segments */}
                        <p className="text-slate-600 leading-relaxed">
                          {para.segments.map((seg, segIdx) => {
                            const globalIdx = paragraphSegmentOffsets[paraIndex] + segIdx;
                            const isActive = globalIdx === activeSegmentIndex;

                            return (
                              <span
                                key={segIdx}
                                ref={isActive ? activeSegmentRef : null}
                                onClick={() => handleSeek(seg.start)}
                                className={`cursor-pointer transition-all rounded px-0.5 -mx-0.5 ${isActive
                                  ? 'bg-amber-200/60 text-slate-800'
                                  : 'hover:bg-slate-100'
                                  }`}
                              >
                                {seg.text}{' '}
                              </span>
                            );
                          })}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    <p className="text-sm">No content available</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// YouTube IFrame API types - augment existing global declarations
// Note: Main YT types are declared in YouTubeEmbed.tsx
